/**
 * The ONE quote wiring for the subscription purchase steps.
 *
 * SubscriptionReviewStep and FinishAccountStep must agree on every number
 * they show — the setup-fee-in-vs-out-of-subtotal divergence this funnel
 * used to have was born as two copies of one calculation drifting, and two
 * copies of the quote wiring would re-arm exactly that. Same reuse argument
 * that put `deriveSubscriptionQuote` in `@butter/library/pricing`, where the
 * mobile confirm page now shares it.
 *
 * Every figure here reads off the server quote — nothing local, and `total`
 * is already fee-inclusive (never size a fee on top of a server total).
 *
 * @module pages/subscription/steps/useSubscriptionPurchasePricing
 */

import { deriveSubscriptionQuote, resolvePaymentRequirement } from '@butter/library/pricing';
import {
  useSubscriptionQuote,
  type FormattedSubscriptionPlan,
} from '~/services/internalApi/subscription';

export function useSubscriptionPurchasePricing(
  plan: FormattedSubscriptionPlan | null,
  clubId: number | undefined,
  promoCode?: string | null,
  /** How many passes the order buys (gifting). Ignored for memberships. */
  quantity: number = 1,
  /**
   * Quote as the signed-in buyer, so a promo code is checked against THEM —
   * per-customer cap, new-customers (D18), enrollment. Pass `isAuthenticated`.
   *
   * Defaults to false: the guest funnel must keep pricing before an account
   * exists, and a step that does not know its auth state should get the
   * preview rather than a 401.
   */
  asMember: boolean = false,
) {
  // NaN → falsy → the query stays disabled (a plan id that doesn't parse is
  // a plan we cannot quote).
  const subscriptionId = plan ? parseInt(String(plan.id), 10) : null;
  const query = useSubscriptionQuote({
    clubId,
    subscriptionType: plan?.subscriptionType ?? null,
    subscriptionId,
    promoCode,
    quantity: plan?.subscriptionType === 'pass' ? quantity : 1,
    asMember,
  });

  // A refusal is a 200 carrying a payload — "the server answered" is true
  // while "the server produced a price" is false.
  const refused = query.data?.isValid === false;
  const quote = deriveSubscriptionQuote({
    quoteEnabled: !!clubId && !!subscriptionId,
    isQuoting: query.isLoading,
    // isError && !data (React Query's isLoadingError): a failed BACKGROUND
    // refetch keeps `data`, and a retained server price is still a real
    // answer — flagging it pending disabled the CTA and unmounted rows over
    // correct prices. `frequent` refetches on focus, so this fires on every
    // tab-switch back with a flaky connection.
    quoteFailed: query.isError && !query.data,
    hasQuoteData: !!query.data,
    total: query.data?.totals?.total ?? 0,
    quoteRefused: refused,
    // The member quote's card verdict (credits D29); absent on a guest quote.
    paymentMethodRequired: query.data?.paymentMethodRequired,
  });
  const requirement = resolvePaymentRequirement(quote);
  const quoted = refused ? undefined : query.data;

  return {
    query,
    refused,
    /**
     * No price on screen AND none coming without help — a first-load failure
     * or an offline PAUSE (networkMode 'online' never calls queryFn offline:
     * fetchStatus 'paused', isError false). The steps' QuoteFailedBanner keys
     * off this, NOT bare `isError`, so a failed background refetch doesn't
     * paint an error over a price that is still valid.
     */
    quoteUnavailable: !query.data && (query.isError || query.isPaused),
    priceUnknown: requirement === 'unknown',
    /**
     * Nothing for a card to pay — the plan is free, or the buyer's account credit covers it
     * (credits X6). Only a member quote can say credit-covered; a guest never sees it.
     */
    noCardNeeded: requirement === 'not-required' || requirement === 'credit-covered',
    creditCovered: requirement === 'credit-covered',
    /** The buyer's account credit below the total (credits D9); member quote only. */
    credit: quoted?.credit,
    subtotal: quoted?.totals?.subtotal ?? 0,
    tax: quoted?.totals?.tax ?? 0,
    total: quoted?.totals?.total ?? 0,
    taxRate: quoted?.totals?.taxRatePercent ?? 0,
    processingFee: quoted?.totals?.processingFee,
    processingFeeRate: quoted?.totals?.processingFeeRate,
    /**
     * The setup-fee row's amount (0 when none). Both steps put this in the
     * confirmation navigation state — Review used to omit it, so an
     * authenticated buyer's receipt never showed the Setup Fee row while a
     * guest's did.
     */
    setupFee: quoted?.lineItems?.find((li) => li.key === 'setup_fee')?.amountDollars ?? 0,
    /**
     * Wire rows for the card, WITHOUT discount rows: PriceSummaryCard's
     * `showSubtotal` reduce sums every row it is given, so a discount row in
     * here would render `subtotal − discount` under a "Subtotal" label. The
     * discount renders via the card's dedicated discount row
     * (`subscriptionDiscount` + `discountLabel`) instead.
     */
    // A row covering several items says so — `Pass: 5 Hour (×2)` — from the
    // server's own `quantity`; the money is never divided client-side.
    lineItems: quoted?.lineItems
      ?.filter((li) => li.key !== 'discount')
      .map((li) => (li.quantity && li.quantity > 1 ? { ...li, label: `${li.label} (×${li.quantity})` } : li)),
    /**
     * Σ discount lines (positive), for the card's discount row and the
     * confirmation nav-state. 0 when no promo applied.
     */
    discountTotal: quoted?.totals?.discountTotal ?? 0,
    /**
     * The promotion block — present iff a code was sent. `applied: false`
     * carries the member `message` (the quote itself stays valid at list
     * price); shown by the chip and near the price cards.
     */
    promotion: quoted?.promotion,
  };
}
